import { useTranslation } from "react-i18next"
import { SignOut } from "@phosphor-icons/react"
import { performLogout, useUser } from "@/apis/bff"
import { SofiaAvatar } from "@/components/sofia-avatar"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import useSwitchLanguage from "@/hooks/useSwitchLanguage"
import { AppearanceMenuSection } from "@/layouts/appearance-section"
import { ReportIssue } from "@/modules/common/report-issue/report-an-issue"
import { SofiaEnvironment } from "@/sofia-environment"
import { UI_DISPLAY_LANGUAGES, UiDisplayLanguage } from "@/types/UiDisplayLanguage"

const languagesRequiringDebugFeatures: UiDisplayLanguage[] = [UI_DISPLAY_LANGUAGES.FRENCH, UI_DISPLAY_LANGUAGES.ITALIAN]

const selectableUiDisplayLanguages = Object.entries(UI_DISPLAY_LANGUAGES).filter(
  ([, languageCode]) =>
    !SofiaEnvironment.AreEndUsersOnEnvironment || !languagesRequiringDebugFeatures.includes(languageCode),
)

const TriggerCloseDrawer = () => {
  document.dispatchEvent(new Event("mousedown", { bubbles: true }))
}

export const UserPopup = () => {
  const [language, switchLanguage] = useSwitchLanguage()
  const { t } = useTranslation()
  const user = useUser()

  const logout = () => {
    performLogout(user)
  }

  return (
    <DropdownMenu onOpenChange={TriggerCloseDrawer}>
      <DropdownMenuTrigger asChild>
        <div className="hover:bg-surface-hovered cursor-pointer rounded-full p-2 transition ease-in-out focus:outline-none">
          <SofiaAvatar firstName={user.givenName} lastName={user.familyName} role={user.backendRole} size="md" />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        data-testid="setting-dropdown-content"
        side="right"
        className="absolute -bottom-5 grid w-56 min-w-75 origin-bottom-left gap-0.5 rounded-lg border-none p-2 leading-5 font-normal">
        <DropdownMenuLabel className="text-on-surface-subdued px-2 py-2.5 text-sm font-medium">
          {t("common.language")}
        </DropdownMenuLabel>
        {selectableUiDisplayLanguages.map(([languageName, languageCode]) => (
          <DropdownMenuCheckboxItem
            key={languageCode}
            data-testid={`menu-language-${languageName.toLowerCase()}`}
            checked={language === languageCode}
            onCheckedChange={() => switchLanguage(languageCode)}>
            {t(`common.menu.${languageName.toLowerCase()}`)}
          </DropdownMenuCheckboxItem>
        ))}
        <DropdownMenuSeparator className="bg-border -mx-2 mt-1.5 mb-0.5" />
        <AppearanceMenuSection />
        <DropdownMenuSeparator className="bg-border -mx-2 mt-1.5 mb-0.5" />
        <ReportIssue />
        <Button
          data-testid="logout-button"
          variant="unstyle"
          className="text-critical hover:bg-critical-surface flex h-8 w-full justify-start rounded-md px-2 py-1 leading-5 font-normal"
          onClick={logout}>
          <SignOut />
          <span>{t("common.button.logout")}</span>
        </Button>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
